import React, { useMemo, useState } from "react";

const flagLabel = (value, lang) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  const isTrue = value === true || normalized === "1" || normalized === "true";
  if (lang === "dk") {
    return isTrue ? "Ja" : "Nej";
  }
  return isTrue ? "Yes" : "No";
};

const COLS = [
  { key: "id", label: "ID" },
  { key: "_date", label: "Date" },
  { key: "_time", label: "Time" },
  { key: "nb_set", label: "Set number" },
  { key: "_card_name", label: "Actioncard name" },
  { key: "_nearMiss", label: "Near miss" },
  { key: "_prevention", label: "Prevention" },
  { key: "_doubtAboutReporting", label: "Doubt about reporting" },
  { key: "comment", label: "Comment" },
  { key: "employee", label: "Employee" },
];

const COL_LABELS = {
  en: {
    id: "ID",
    _date: "Date",
    _time: "Time",
    nb_set: "Set number",
    _card_name: "Actioncard name",
    _nearMiss: "Near miss",
    _prevention: "Prevention",
    _doubtAboutReporting: "Doubt about reporting",
    comment: "Comment",
    employee: "Employee",
  },
  dk: {
    id: "ID",
    _date: "Dato",
    _time: "Tid",
    nb_set: "Sætnummer",
    _card_name: "Actioncard navn",
    _nearMiss: "Near miss",
    _prevention: "Forebyggelse",
    _doubtAboutReporting: "Tvivl om rapportering",
    comment: "Kommentar",
    employee: "Medarbejder",
  },
};

const SortIcon = ({ active, dir }) => (
  <span
    style={{
      display: "inline-flex",
      flexDirection: "column",
      marginLeft: 4,
      lineHeight: 1,
      verticalAlign: "middle",
    }}
  >
    <span
      style={{
        opacity: active && dir === "asc" ? 1 : 0.25,
        fontSize: 8,
        lineHeight: 1,
      }}
    >
      ▲
    </span>
    <span
      style={{
        opacity: active && dir === "desc" ? 1 : 0.25,
        fontSize: 8,
        lineHeight: 1,
      }}
    >
      ▼
    </span>
  </span>
);

const ReportingCategoryTable = ({ records = [], actionCards = [], lang = "en" }) => {
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  const actionCardLookup = useMemo(() => {
    const lookup = {};
    actionCards.forEach((card) => {
      lookup[String(card.ac_no)] = {
        en: card.english_short_name,
        dk: card.danish_short_name,
      };
    });
    return lookup;
  }, [actionCards]);

  const localizedCols = useMemo(
    () =>
      COLS.map((col) => ({
        ...col,
        label: COL_LABELS[lang]?.[col.key] ?? COL_LABELS.en[col.key] ?? col.label,
      })),
    [lang]
  );

  const enriched = useMemo(
    () =>
      records.map((record) => {
        const [datePart, timePart] = (record.utc_tid_skannet ?? "").split(" ");
        return {
          ...record,
          _date: datePart ?? "-",
          _time: timePart ?? "-",
          _card_name:
            actionCardLookup[String(record.action_card_number)]?.[lang] ??
            record.action_card_number ??
            "-",
          _nearMiss: flagLabel(record.has_never_miss, lang),
          _prevention: flagLabel(record.has_prevention, lang),
          _doubtAboutReporting: flagLabel(record.has_doubt_about_reporting, lang),
        };
      }),
    [records, actionCardLookup, lang]
  );

  const searched = useMemo(() => {
    const query = search.toLowerCase().trim();
    if (!query) return enriched;

    return enriched.filter((record) =>
      localizedCols.some((col) => {
        const value = record[col.key];
        return value != null && String(value).toLowerCase().includes(query);
      })
    );
  }, [enriched, localizedCols, search]);

  const sorted = useMemo(
    () =>
      [...searched].sort((a, b) => {
        const av = a[sortKey] ?? "";
        const bv = b[sortKey] ?? "";
        const cmp =
          typeof av === "number"
            ? av - bv
            : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      }),
    [searched, sortKey, sortDir]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sorted.length / pageSize)),
    [sorted.length, pageSize]
  );

  const safePage = useMemo(() => Math.min(page, totalPages - 1), [page, totalPages]);

  const paginated = useMemo(
    () => sorted.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [sorted, safePage, pageSize]
  );

  const pageButtons = useMemo(() => {
    const start = Math.max(0, safePage - 2);
    const end = Math.min(totalPages - 1, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [safePage, totalPages]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const startEntry = sorted.length === 0 ? 0 : safePage * pageSize + 1;
  const endEntry = Math.min((safePage + 1) * pageSize, sorted.length);

  const thStyle = {
    padding: "10px 12px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 13,
    color: "#333",
    borderBottom: "2px solid #ddd",
    cursor: "pointer",
    whiteSpace: "nowrap",
    userSelect: "none",
    background: "#fafafa",
  };

  const tdStyle = {
    padding: "9px 12px",
    textAlign: "center",
    fontSize: 13,
    color: "#444",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  };

  const commentTdStyle = {
    ...tdStyle,
    whiteSpace: "normal",
    wordBreak: "break-word",
    textAlign: "left",
    minWidth: 260,
    maxWidth: 360,
  };

  const btnBase = {
    padding: "4px 10px",
    border: "1px solid #ccc",
    borderRadius: 3,
    cursor: "pointer",
    fontSize: 12,
    background: "#fff",
    color: "#333",
  };
  const btnActive = {
    ...btnBase,
    background: "#337ab7",
    color: "#fff",
    borderColor: "#2e6da4",
  };
  const btnDisabled = { ...btnBase, opacity: 0.45, cursor: "default" };

  return (
    <div style={{ fontFamily: "sans-serif", marginTop: 8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span>{lang === "dk" ? "Vis" : "Show"}</span>
          <select
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(0);
            }}
            style={{
              padding: "3px 6px",
              border: "1px solid #ccc",
              borderRadius: 3,
              fontSize: 13,
            }}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span>{lang === "dk" ? "rækker" : "entries"}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span>{lang === "dk" ? "Søg:" : "Search:"}</span>
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            style={{
              padding: "4px 8px",
              border: "1px solid #ccc",
              borderRadius: 3,
              fontSize: 13,
              width: 200,
            }}
          />
        </div>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1460, tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 70 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 170 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 170 }} />
            <col style={{ width: 320 }} />
            <col style={{ width: 150 }} />
          </colgroup>
          <thead>
            <tr>
              {localizedCols.map((col) => (
                <th key={col.key} style={thStyle} onClick={() => handleSort(col.key)}>
                  {col.label}
                  <SortIcon active={sortKey === col.key} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={localizedCols.length} style={{ ...tdStyle, padding: 28, color: "#999" }}>
                  {lang === "dk" ? "Ingen data fundet" : "No records found"}
                </td>
              </tr>
            ) : (
              paginated.map((row, index) => (
                <tr key={row.id} style={{ background: index % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                  <td style={tdStyle}>{row.id}</td>
                  <td style={tdStyle}>{row._date}</td>
                  <td style={tdStyle}>{row._time}</td>
                  <td style={tdStyle}>{row.nb_set}</td>
                  <td style={tdStyle}>{row._card_name}</td>
                  <td style={tdStyle}>{row._nearMiss}</td>
                  <td style={tdStyle}>{row._prevention}</td>
                  <td style={tdStyle}>{row._doubtAboutReporting}</td>
                  <td style={commentTdStyle}>{row.comment || "—"}</td>
                  <td style={tdStyle}>{row.employee}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              {localizedCols.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...thStyle,
                    borderTop: "2px solid #ddd",
                    borderBottom: "none",
                    fontWeight: 500,
                    fontSize: 11,
                    color: "#888",
                    cursor: "default",
                  }}
                >
                  {col.label.length > 14 ? `${col.label.substring(0, 14)}…` : col.label}
                </th>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 13,
          color: "#555",
        }}
      >
        <div>
          {lang === "dk" ? "Viser" : "Showing"} {startEntry} {lang === "dk" ? "til" : "to"} {endEntry}{" "}
          {lang === "dk" ? "af" : "of"} {sorted.length} {lang === "dk" ? "rækker" : "entries"}
        </div>

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            disabled={safePage === 0}
            style={safePage === 0 ? btnDisabled : btnBase}
          >
            {lang === "dk" ? "Forrige" : "Prev"}
          </button>

          {pageButtons.map((pageNumber) => (
            <button
              type="button"
              key={pageNumber}
              onClick={() => setPage(pageNumber)}
              style={pageNumber === safePage ? btnActive : btnBase}
            >
              {pageNumber + 1}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
            disabled={safePage === totalPages - 1}
            style={safePage === totalPages - 1 ? btnDisabled : btnBase}
          >
            {lang === "dk" ? "Næste" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportingCategoryTable;
